/**
 * Small modal shown ONCE after an admin logs in — asks whether to go to
 * the admin panel or the user panel. The choice is remembered in
 * localStorage under `veerox_admin_pref` (skipped next login).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Users } from "lucide-react";

const PREF_KEY = "veerox_admin_pref";

export const getAdminPref = () => localStorage.getItem(PREF_KEY);
export const setAdminPref = (v) => localStorage.setItem(PREF_KEY, v);
export const clearAdminPref = () => localStorage.removeItem(PREF_KEY);

export default function AdminChoiceModal({ open, onDone }) {
  const navigate = useNavigate();
  const [remember, setRemember] = useState(true);
  if (!open) return null;

  const choose = (dest) => {
    if (remember) setAdminPref(dest === "/admin" ? "admin" : "user");
    sessionStorage.removeItem("veerox_admin_choice_pending");
    onDone?.();
    navigate(dest, { replace: true });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      data-testid="admin-choice-modal"
    >
      <div className="w-[min(90vw,480px)] rounded-lg border border-[#dc2626]/60 bg-[#111827] shadow-2xl">
        <div className="px-5 py-4 border-b border-[#1f2937] flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#dc2626]" />
          <div className="font-heading font-black tracking-tighter">Admin login detected</div>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-[#e5e7eb]">
            You have access to both the admin console and the user panel. Which would you like to open?
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => choose("/admin")}
              className="rounded-lg border border-[#dc2626]/40 bg-[#dc2626]/10 hover:bg-[#dc2626]/20 p-4 text-left transition-colors"
              data-testid="choose-admin-panel"
            >
              <Shield className="w-5 h-5 text-[#dc2626] mb-2" />
              <div className="font-semibold text-sm">Admin Panel</div>
              <div className="text-[10px] text-[#9ca3af] font-mono mt-1">User mgmt · analytics · impersonation</div>
            </button>
            <button
              onClick={() => choose("/dashboard")}
              className="rounded-lg border border-[#3b82f6]/40 bg-[#3b82f6]/10 hover:bg-[#3b82f6]/20 p-4 text-left transition-colors"
              data-testid="choose-user-panel"
            >
              <Users className="w-5 h-5 text-[#3b82f6] mb-2" />
              <div className="font-semibold text-sm">User Panel</div>
              <div className="text-[10px] text-[#9ca3af] font-mono mt-1">View the trading dashboard as a user</div>
            </button>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#9ca3af] font-mono">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                   data-testid="admin-choice-remember" />
            Remember my choice next time
          </label>
        </div>
      </div>
    </div>
  );
}
